import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Mail, MessageSquare, Image as ImageIcon, Calendar, Loader2,
  ArrowLeft, Sparkles, TrendingUp, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATUS_COLORS = {
  draft:     'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  sending:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  sent:      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed:    'bg-rose-500/15 text-rose-300 border-rose-500/30',
  scheduled: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
};

/**
 * /organization/marketing — lists every campaign + SMS blast attached to
 * one of this organization's events. Powers the "Run a campaign" CTA
 * that pulls organizers from the dashboard into a real org-scoped surface.
 */
export default function OrganizationMarketing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/organizations/me/marketing`, auth());
      setData(r.data);
    } catch (e) {
      navigate('/organization/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  const { events, totals } = data;

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="organization-marketing-page">
      <div className="max-w-6xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link
              to="/organization/dashboard"
              className="text-xs text-muted-foreground hover:text-indigo-400 inline-flex items-center gap-1 mb-2"
              data-testid="back-to-dashboard-link"
            >
              <ArrowLeft className="h-3 w-3" /> Back to dashboard
            </Link>
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
              Marketing
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] mt-1">
              My events & campaigns
            </h1>
          </div>
          <Link to="/organization/dashboard">
            <Button
              variant="outline"
              className="border-indigo-500/40 hover:bg-indigo-500/10"
              data-testid="topup-wallet-shortcut"
            >
              <Wallet className="h-4 w-4 mr-2" /> Wallet
            </Button>
          </Link>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="marketing-totals">
          <Stat icon={Calendar}        label="Events"        value={totals.events} />
          <Stat icon={Mail}             label="Campaigns"      value={totals.campaigns} />
          <Stat icon={MessageSquare}   label="SMS blasts"     value={totals.blasts} />
          <Stat icon={TrendingUp}      label="Emails sent"    value={totals.emails_sent} />
          <Stat icon={Wallet}          label="Wallet spent"
                value={fmtUSD(totals.wallet_spent_cents)} valueMono />
        </div>

        {/* Empty state */}
        {events.length === 0 && (
          <Card className="border-dashed border-2 border-indigo-500/30" data-testid="marketing-empty">
            <CardContent className="p-8 text-center space-y-3">
              <Sparkles className="h-10 w-10 mx-auto text-indigo-400" />
              <h3 className="text-xl font-bold">No events yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Create your first event to start running email and SMS
                campaigns. Your wallet funds every send and every AI generation.
              </p>
              <Link to="/admin/events?as=organizer">
                <Button className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white">
                  Create event
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Per-event accordion */}
        {events.map((ev) => <EventBlock key={ev.event_id} event={ev} />)}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, valueMono }) {
  return (
    <Card className="border-border" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <Icon className="h-3 w-3" /> {label}
        </div>
        <p className={`text-2xl font-bold mt-0.5 ${valueMono ? 'tabular-nums' : ''}`}>
          {value ?? 0}
        </p>
      </CardContent>
    </Card>
  );
}

function EventBlock({ event }) {
  const ev = event;
  const campaigns = ev.campaigns || [];
  const blasts = ev.blasts || [];
  const total = campaigns.length + blasts.length;

  return (
    <Card className="border-border" data-testid={`event-block-${ev.event_id}`}>
      <CardContent className="p-0">
        {/* Event header */}
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-bold truncate flex items-center gap-2">
              {ev.name}
              {ev.is_published
                ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px]">Published</Badge>
                : <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/30 border text-[10px]">Draft</Badge>
              }
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {ev.event_date
                ? new Date(ev.event_date).toLocaleDateString(undefined,
                    { month: 'short', day: 'numeric', year: 'numeric' })
                : 'No date'}
              {ev.city ? ` · ${ev.city}` : ''}
              {total > 0 && ` · ${total} marketing item${total === 1 ? '' : 's'}`}
            </p>
          </div>
          <Link
            to={`/events/${ev.city || 'denver'}/${ev.slug}`}
            target="_blank"
            data-testid={`view-event-${ev.event_id}`}
          >
            <Button variant="outline" size="sm">View event</Button>
          </Link>
        </div>

        {/* Campaigns */}
        {campaigns.length > 0 && (
          <div data-testid={`event-${ev.event_id}-campaigns`}>
            <SubHeader icon={Mail} label="Email campaigns" count={campaigns.length} />
            <div className="divide-y divide-border">
              {campaigns.map((c) => (
                <MarketingRow
                  key={c.campaign_id}
                  title={c.name || c.subject}
                  subtitle={c.subject}
                  status={c.status}
                  meta={[
                    { label: 'Sent', value: (c.stats?.sent ?? 0).toLocaleString() },
                    c.stats?.opened ? { label: 'Opened', value: c.stats.opened } : null,
                    { label: 'Created', value: c.created_at ? new Date(c.created_at).toLocaleDateString() : '' },
                  ]}
                  testid={`campaign-row-${c.campaign_id}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Blasts */}
        {blasts.length > 0 && (
          <div data-testid={`event-${ev.event_id}-blasts`}>
            <SubHeader icon={MessageSquare} label="SMS blasts" count={blasts.length} />
            <div className="divide-y divide-border">
              {blasts.map((b) => (
                <MarketingRow
                  key={b.blast_id}
                  title={b.name || 'SMS blast'}
                  subtitle={b.body?.slice(0, 100)}
                  status={b.status}
                  badges={b.media_url ? ['MMS'] : []}
                  meta={[
                    { label: 'Sent', value: (b.stats?.sent ?? 0).toLocaleString() },
                    b.org_wallet_charged_cents != null
                      ? { label: 'Charged', value: fmtUSD(b.org_wallet_charged_cents) }
                      : null,
                    { label: 'Created', value: b.created_at ? new Date(b.created_at).toLocaleDateString() : '' },
                  ]}
                  testid={`blast-row-${b.blast_id}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty per-event */}
        {total === 0 && (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground" data-testid={`event-${ev.event_id}-empty`}>
            No campaigns or blasts yet for this event.
            <Link
              to={`/admin/events?as=organizer&event_id=${ev.event_id}`}
              className="ml-2 text-indigo-400 underline"
            >
              Create one
            </Link>
          </div>
        )}

        {/* Actions */}
        <div className="px-5 py-3 border-t border-border flex flex-wrap items-center gap-2">
          <Link to={`/admin/events?as=organizer&event_id=${ev.event_id}#campaigns`}>
            <Button size="sm" variant="outline" data-testid={`new-campaign-${ev.event_id}`}>
              <Mail className="h-3.5 w-3.5 mr-1" /> New email campaign
            </Button>
          </Link>
          <Link to={`/admin/events?as=organizer&event_id=${ev.event_id}#blasts`}>
            <Button size="sm" variant="outline" data-testid={`new-blast-${ev.event_id}`}>
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> New SMS blast
            </Button>
          </Link>
          <Link to={`/admin/events?as=organizer&event_id=${ev.event_id}#ai`}>
            <Button size="sm" variant="outline" data-testid={`ai-tools-${ev.event_id}`}>
              <ImageIcon className="h-3.5 w-3.5 mr-1" /> AI tools
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function SubHeader({ icon: Icon, label, count }) {
  return (
    <div className="px-5 py-2 bg-card/40 border-b border-border flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-indigo-300" />
      <h4 className="text-xs font-bold uppercase tracking-wider">{label}</h4>
      <span className="text-[10px] text-muted-foreground">({count})</span>
    </div>
  );
}

function MarketingRow({ title, subtitle, status, meta = [], badges = [], testid }) {
  return (
    <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm truncate">{title || 'Untitled'}</p>
          <Badge className={`${STATUS_COLORS[status] || ''} border text-[10px]`}>{status}</Badge>
          {badges.map((b) => (
            <Badge key={b} className="bg-indigo-500/10 text-indigo-200 border-indigo-500/20 border text-[10px]">{b}</Badge>
          ))}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {meta.filter(Boolean).map((m) => (
          <span key={m.label} className="flex items-center gap-1 tabular-nums">
            <strong className="text-foreground">{m.value}</strong>
            <span className="text-[10px]">{m.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
